
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ViewState, Language, Director } from './types';
import { Navigation } from './components/Navigation';
import { Hero } from './components/Hero';
import { Services } from './components/Services';
import { Directors } from './components/Directors';
import { FilmmakerPortfolio } from './components/FilmmakerPortfolio';
import { About } from './components/About';
import { ScriptStudio } from './components/ScriptStudio';
import { SignatureGenerator } from './components/SignatureGenerator';
import { translations } from './utils/translations';
import { CustomCursor } from './components/CustomCursor';
import { filmmakersContent } from './data/filmmakersContent';
import { BackToTop } from './components/BackToTop';
import { slugify } from './utils/slugify';

const SplashScreen: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 3000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-[1000] bg-black flex flex-col items-center justify-center scanlines p-6">
      <div className="relative group animate-pulse">
        <img 
          src="https://www.irreverentia.eu/logo-green.png" 
          alt="IRREVERENTIA" 
          className="w-64 md:w-96 h-auto object-contain drop-shadow-[0_0_20px_#ccff00] animate-logo-jitter"
        />
      </div>
      <div className="mt-12 flex flex-col items-center space-y-4">
        <div className="overflow-hidden">
          <p className="font-mono text-acid text-[10px] md:text-sm tracking-[0.4em] uppercase whitespace-nowrap border-r-2 border-acid animate-typing-mobile-1">
            DISRUPTIVE STORYTELLING
          </p>
        </div>
        <div className="overflow-hidden">
          <p className="font-mono text-acid text-[10px] md:text-sm tracking-[0.4em] uppercase whitespace-nowrap border-r-2 border-acid animate-typing-mobile-2">
            POWERED BY AI
          </p>
        </div>
      </div>
      <div className="absolute bottom-10 font-mono text-[8px] text-zinc-800 tracking-widest uppercase">
        SYSTEM_INIT // NEURAL_LOADER_v4.0
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewState>(ViewState.HOME);
  const [language, setLanguage] = useState<Language>('ES');
  const [selectedDirector, setSelectedDirector] = useState<Director | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [showSplash, setShowSplash] = useState(true);

  const supportedLangs: Language[] = useMemo(() => ['EN', 'ES', 'PT', 'FR', 'DE'], []);

  // SEO: Update Head Metadata and Structured Data
  const updateSEOMetadata = useCallback((view: ViewState, lang: Language, director: Director | null) => {
    const t = translations[lang];
    let title = t.meta.title;
    let desc = t.hero.tagline;

    if (director && view === ViewState.FILMMAKER_PORTFOLIO) {
      title = `${director.name} | ${director.role} | ¡RR3VERENT!A`;
      desc = director.desc;
    }

    document.title = title;
    
    // Update Meta Description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', desc);

    // Update OpenGraph
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    if (ogDesc) ogDesc.setAttribute('content', desc);

    // Inyectar JSON-LD dinámico para SEO semántico
    const existingScript = document.getElementById('json-ld-schema');
    if (existingScript) existingScript.remove();

    const schema = {
      "@context": "https://schema.org",
      "@type": "VideoProductionService",
      "name": "¡RR3VERENT!A",
      "description": t.hero.tagline,
      "url": "https://irreverentia.eu",
      "logo": "https://www.irreverentia.eu/logo-green.png",
      "knowsAbout": ["Artificial Intelligence", "Film Production", "CGI", "Generative AI"],
      "areaServed": "Global"
    };

    const script = document.createElement('script');
    script.id = 'json-ld-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
  }, []);

  const syncStateFromUrl = useCallback(() => {
    const fullHash = window.location.hash.replace(/^#/, '');
    const cleanPath = fullHash.replace(/^\/+|\/+$/g, '');
    const pathParts = cleanPath.split('/').filter(Boolean);
    
    let detectedLang: Language = 'ES';
    let baseIndex = 0;
    
    if (pathParts[0] && supportedLangs.includes(pathParts[0].toUpperCase() as Language)) {
      detectedLang = pathParts[0].toUpperCase() as Language;
      baseIndex = 1;
    }
    setLanguage(detectedLang);

    const viewPath = pathParts[baseIndex];
    if (viewPath) {
      const normalizedPath = viewPath.toUpperCase();
      const content = filmmakersContent.find(f => f.id === viewPath.toLowerCase());
      
      if (content) {
        const directorObj: Director = {
          id: content.id,
          name: content.name,
          role: content.roles[detectedLang],
          desc: content.bios[detectedLang],
          img: content.img
        };
        setSelectedDirector(directorObj);
        setCurrentView(ViewState.FILMMAKER_PORTFOLIO);
        updateSEOMetadata(ViewState.FILMMAKER_PORTFOLIO, detectedLang, directorObj);
        
        // Track and sync selected project
        const projectPath = pathParts[baseIndex + 1];
        if (projectPath) {
          const matchProject = content.portfolio.find(
            p => p.id.toString() === projectPath || slugify(p.title) === projectPath.toLowerCase()
          );
          if (matchProject) {
            setSelectedProjectId(matchProject.id);
          } else {
            setSelectedProjectId(null);
          }
        } else {
          setSelectedProjectId(null);
        }
      } else if (normalizedPath === 'STUDIO') {
        setSelectedProjectId(null);
        setCurrentView(ViewState.STUDIO_CHAT);
        updateSEOMetadata(ViewState.STUDIO_CHAT, detectedLang, null);
      } else if (normalizedPath === 'SIGNATURE') {
        setSelectedProjectId(null);
        setCurrentView(ViewState.SIGNATURE_GENERATOR);
        updateSEOMetadata(ViewState.SIGNATURE_GENERATOR, detectedLang, null);
      } else if (normalizedPath === 'REEL') {
        setSelectedProjectId(null);
        setCurrentView(ViewState.DEMO_REEL);
        updateSEOMetadata(ViewState.DEMO_REEL, detectedLang, null);
      } else {
        setSelectedProjectId(null);
        setCurrentView(ViewState.HOME);
        updateSEOMetadata(ViewState.HOME, detectedLang, null);
      }
    } else {
      setSelectedProjectId(null);
      setCurrentView(ViewState.HOME);
      setSelectedDirector(null);
      updateSEOMetadata(ViewState.HOME, detectedLang, null);
    }
  }, [supportedLangs, updateSEOMetadata]);

  const updateUrl = (
    lang: Language, 
    view: ViewState, 
    filmmaker: Director | null, 
    projectId: number | null = selectedProjectId
  ) => {
    const langPart = lang.toLowerCase();
    let suffix = '';
    
    if (filmmaker) {
      suffix = `/${filmmaker.id}`;
      if (projectId) {
        const content = filmmakersContent.find(f => f.id === filmmaker.id);
        const proj = content?.portfolio.find(p => p.id === projectId);
        if (proj) {
          suffix += `/${slugify(proj.title)}`;
        }
      }
    } else if (view === ViewState.STUDIO_CHAT) {
      suffix = '/studio';
    } else if (view === ViewState.SIGNATURE_GENERATOR) {
      suffix = '/signature';
    } else if (view === ViewState.DEMO_REEL) {
      suffix = '/reel';
    }
    
    const newHash = `/${langPart}${suffix}`;
    if (window.location.hash !== `#${newHash}`) {
      window.location.hash = newHash;
    }
  };

  useEffect(() => {
    syncStateFromUrl();
    window.addEventListener('hashchange', syncStateFromUrl);
    return () => window.removeEventListener('hashchange', syncStateFromUrl);
  }, [syncStateFromUrl]);

  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang);
    updateUrl(newLang, currentView, selectedDirector);
    updateSEOMetadata(currentView, newLang, selectedDirector);
  };

  const handleViewChange = (view: ViewState) => {
    setCurrentView(view);
    if (view !== ViewState.FILMMAKER_PORTFOLIO) setSelectedDirector(null);
    updateUrl(language, view, null);
    updateSEOMetadata(view, language, null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDirectorSelect = (director: Director) => {
    setSelectedDirector(director);
    setCurrentView(ViewState.FILMMAKER_PORTFOLIO);
    updateUrl(language, ViewState.FILMMAKER_PORTFOLIO, director);
    updateSEOMetadata(ViewState.FILMMAKER_PORTFOLIO, language, director);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToDirectors = () => {
    setCurrentView(ViewState.HOME);
    setSelectedDirector(null);
    updateUrl(language, ViewState.HOME, null);
    updateSEOMetadata(ViewState.HOME, language, null);
    
    setTimeout(() => {
      const element = document.getElementById('directors');
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
      }
    }, 100);
  };

  const renderView = () => {
    switch (currentView) {
      case ViewState.HOME:
        return (
          <div className="flex flex-col">
            <div id="home"><Hero setView={handleViewChange} language={language} /></div>
            <div id="services"><Services language={language} /></div>
            <div id="directors"><Directors language={language} onSelect={handleDirectorSelect} setView={handleViewChange} /></div>
            <div id="contact"><About language={language} /></div>
          </div>
        );
      case ViewState.FILMMAKER_PORTFOLIO:
        return (
          <FilmmakerPortfolio 
            director={selectedDirector} 
            onBack={handleBackToDirectors} 
            activeProjectId={selectedProjectId}
            onSelectProject={(id) => {
              setSelectedProjectId(id);
              updateUrl(language, ViewState.FILMMAKER_PORTFOLIO, selectedDirector, id);
            }}
            language={language}
          />
        );
      case ViewState.STUDIO_CHAT:
        return <ScriptStudio />;
      case ViewState.SIGNATURE_GENERATOR:
        return <SignatureGenerator />;
      case ViewState.DEMO_REEL:
        return (
          <div className="flex flex-col">
            <div id="home"><Hero setView={handleViewChange} language={language} isReelOpen={true} /></div>
            <div id="services"><Services language={language} /></div>
            <div id="directors"><Directors language={language} onSelect={handleDirectorSelect} setView={handleViewChange} /></div>
            <div id="contact"><About language={language} /></div>
          </div>
        );
      default:
        return <Hero setView={handleViewChange} language={language} />;
    }
  };

  return (
    <div className="min-h-screen bg-void text-zinc-100 selection:bg-acid selection:text-void font-sans flex flex-col cursor-none">
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      <CustomCursor />
      <BackToTop />
      {!showSplash && (
        <>
          <Navigation 
            currentView={currentView} 
            setView={handleViewChange} 
            language={language}
            setLanguage={handleLanguageChange}
          />
          
          <main className="flex-grow w-full">
            {renderView()}
          </main>
          
          <footer className="fixed bottom-0 left-0 w-full bg-void/90 backdrop-blur-md border-t border-zinc-900 py-2 px-6 z-40 hidden md:flex justify-between items-center text-[10px] font-mono text-zinc-600 uppercase tracking-widest">
             <div className="flex items-center gap-4">
               <span className="flex items-center gap-2">
                 <span className="w-1.5 h-1.5 bg-acid rounded-full animate-pulse"></span>
                 STATUS: ONLINE
               </span>
               <span>LATENCY: 12MS</span>
             </div>
             <span>¡RR3VERENT!A STUDIOS © 2025</span>
          </footer>
        </>
      )}
    </div>
  );
};

export default App;
